import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";
import { isAxiosError } from "axios";

export interface LeaveTypeRecord {
  id: ID;
  title: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

const LS_KEY = "inventory.leaveTypes.v1";

type LeaveTypeApiRecord = {
  id?: number | string;
  title?: string | null;
  slug?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = { message?: string; data?: LeaveTypeApiRecord };

function dedupeById(items: LeaveTypeRecord[]): LeaveTypeRecord[] {
  const byId = new Map<ID, LeaveTypeRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function mapApiRecord(record: LeaveTypeApiRecord): LeaveTypeRecord {
  return {
    id: String(record.id ?? ""),
    title: String(record.title ?? "").trim(),
    slug: String(record.slug ?? "").trim(),
    createdAt: String(record.created_at ?? new Date().toISOString()),
    updatedAt: String(record.updated_at ?? record.created_at ?? new Date().toISOString()),
  };
}

function load(): LeaveTypeRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Partial<LeaveTypeRecord>[];
    if (!Array.isArray(parsed)) return [];
    return dedupeById(parsed as LeaveTypeRecord[]);
  } catch {
    return [];
  }
}

function save(items: LeaveTypeRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): LeaveTypeApiRecord[] {
  if (Array.isArray(body)) return body as LeaveTypeApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): LeaveTypeApiRecord[] | null => {
    if (Array.isArray(block)) return block as LeaveTypeApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as LeaveTypeApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "items", "leave_types", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): LeaveTypeApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data) && "id" in data) {
    return data as LeaveTypeApiRecord;
  }
  return null;
}

export function leaveTypeErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong. Please try again.";
}

export function listLeaveTypeRecords(): LeaveTypeRecord[] {
  return load().sort((a, b) => a.title.localeCompare(b.title) || String(a.id).localeCompare(String(b.id)));
}

export function listActiveLeaveTypeRecords(): LeaveTypeRecord[] {
  // Active flag removed; all are selectable.
  return listLeaveTypeRecords();
}

export function getLeaveTypeById(id: ID): LeaveTypeRecord | undefined {
  return load().find((t) => t.id === id);
}

export function codeExists(code: string, exceptId?: ID): boolean {
  const c = slugify(code);
  return load().some((t) => slugify(t.slug) === c && t.id !== exceptId);
}

const PER_PAGE = 50;

export async function syncLeaveTypesFromApi(): Promise<LeaveTypeRecord[]> {
  const collected: LeaveTypeApiRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await api.get("/api/v1/leave-types", {
      params: { page, per_page: PER_PAGE },
    });
    const body = response.data;
    collected.push(...extractRowsFromResponse(body));
    lastPage = readLastPageFromListBody(body) ?? 1;
    page += 1;
  } while (page <= lastPage && page <= 50);

  const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
  save(records);
  return listLeaveTypeRecords();
}

export async function createLeaveType(data: { title: string; slug?: string }): Promise<{ record: LeaveTypeRecord; message: string }> {
  const items = load();
  const payload = {
    title: data.title.trim(),
    slug: (data.slug ?? "").trim() || slugify(data.title),
  };
  const response = await api.post<UpsertApiResponse>("/api/v1/leave-types", payload);
  const created = extractFromUpsertBody(response.data);
  if (!created?.id) throw new Error("Invalid create leave type response.");
  const record = mapApiRecord(created);
  const i = items.findIndex((x) => x.id === record.id);
  if (i >= 0) items[i] = record;
  else items.push(record);
  save(items);
  return { record, message: response.data?.message || "Leave type created." };
}

export async function updateLeaveType(
  id: ID,
  data: { title: string; slug: string },
): Promise<{ record: LeaveTypeRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((t) => t.id === id);
  if (idx === -1) return null;
  const payload = {
    title: data.title.trim(),
    slug: data.slug.trim() || slugify(data.title),
  };
  const response = await api.put<UpsertApiResponse>(`/api/v1/leave-types/${id}`, payload);
  const updated = extractFromUpsertBody(response.data);
  if (!updated?.id) throw new Error("Invalid update leave type response.");
  items[idx] = mapApiRecord(updated);
  save(items);
  return { record: items[idx], message: response.data?.message || "Leave type updated." };
}

export async function deleteLeaveType(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<{ message?: string }>(`/api/v1/leave-types/${id}`);
  const items = load();
  const next = items.filter((t) => t.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message: response.data?.message || (removed ? "Leave type deleted." : "Not found."),
  };
}
